import { isRegularPosition, isGoodsPosition, isComparablePosition } from "./offerPositionKind";
import type { OfferPreview } from "../api/offerPreview";
import { toNumber } from "./format";

export type ReviewOffer = { id: string; name: string; preview: OfferPreview; docId?: string; originalUrl?: string };
export type ReviewLine = OfferPreview["lines"][number];
export type ComparisonRow = { key: string; lines: Array<ReviewLine | null>; common: boolean; reason: string };

export function offerComparison(offers: ReviewOffer[]): ComparisonRow[] {
  const duplicate = offers.some((offer, index) => offers.slice(0, index).some(previous => Boolean(offer.preview.source_sha256) && offer.preview.source_sha256 === previous.preview.source_sha256));
  const groups = new Map<string, Array<ReviewLine[]>>();
  offers.forEach((offer, index) => offer.preview.lines.forEach((line, position) => {
    if (line.source_note) return;
    const key = offer.preview.parser_kind === "danfoss_offer_v1" ? `single-review:${index}:${position}` : !isRegularPosition(line) ? `variant:${index}:${position}` : !isGoodsPosition(line) ? `non-goods:${index}:${position}` : line.comparison_key ?? `unmatched:${index}:${position}`;
    const group = groups.get(key) ?? offers.map(() => []);
    group[index].push(line); groups.set(key, group);
  }));
  return [...groups].flatMap<ComparisonRow>(([key, groups]) => {
    // Repeated keys are ambiguous, not quantity buckets. Keep each source row.
    if (groups.some(group => group.length > 1)) return groups.flatMap((group, index) => group.map((line, position) => ({
      key: `${key}:${index}:${position}`, lines: offers.map((_, i) => i === index ? line : null), common: false, reason: "Artikel mehrfach enthalten – Zuordnung prüfen",
    })));
    const lines = groups.map(group => group[0] ?? null);
    const complete = lines.every(line => line !== null);
    const valid = complete && lines.every(line => isComparablePosition(line!) && !line!.comparison_warning && toNumber(line!.net_unit_price) !== null && toNumber(line!.line_net_amount) !== null);
    const quantities = new Set(lines.map(line => toNumber(line?.qty)));
    const sameUnits = new Set(lines.map(line => line?.uom?.trim().toUpperCase())).size === 1;
    const sameCurrency = new Set(offers.map(offer => offer.preview.currency)).size === 1 && Boolean(offers[0]?.preview.currency);
    const common = !duplicate && offers.length > 1 && valid && quantities.size === 1 && sameUnits && sameCurrency;
    return [{ key, lines, common, reason: lines.some(line => line && !isRegularPosition(line)) ? "Alternative oder Optionalartikel – einzeln prüfen, nicht in der gemeinsamen Summe" : lines.some(line => line && !isGoodsPosition(line)) ? "Leistungsumfang oder Positionsart separat prüfen" : !complete ? "Nicht in allen Angeboten eindeutig zugeordnet" : !sameUnits || !sameCurrency ? "Einheit oder Währung unterscheiden sich" : !valid ? "Angaben im Original prüfen" : quantities.size !== 1 ? "Unterschiedliche Mengen – Einzelpreise vergleichen" : "Gleiche Artikel und Mengen" }];
  });
}

export function sourceTotal(preview: OfferPreview): number | null {
  const rows = preview.lines.filter(isRegularPosition);
  if (preview.parser_kind && !preview.sum_comparable) return null;
  if (!rows.length || rows.some(line => toNumber(line.line_net_amount) === null)) return null;
  return sumOfferAmounts(rows.map(line => line.line_net_amount));
}

export function money(value: string | number | null | undefined, currency: string | null) {
  const numeric = toNumber(value);
  if (numeric === null) return "–";
  return `${new Intl.NumberFormat("de-AT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numeric)} ${currency ?? ""}`.trim();
}

// Printed position amounts are displayed in cents. Accumulate cents before
// ranking, so equal displayed totals remain tied regardless of binary rounding.
export function sumOfferAmounts(values: Array<string | number | null>): number {
  return values.reduce<number>((sum, value) => sum + Math.round(Number(value) * 100), 0) / 100;
}
